import { useSyncExternalStore } from "react";

/**
 * Whether the Job Envoy extension is in this browser. Its app page script
 * says it is here as soon as the page loads, and again when the page asks
 * (`auth/extension.ts`), so a word from it means it is installed. The one
 * thing kept is that this browser had it last time (`hadExtension`), so
 * the banner can wait for it to say so instead of flashing.
 *
 * "Do not ask again" on the install hint is the person's own choice, kept
 * in this browser only.
 */
let seen = false;
const listeners = new Set<() => void>();

export function markExtensionSeen(): void {
    if (seen) return;
    seen = true;
    listeners.forEach((listener) => listener());
}

/** Whether the extension was seen on this page, outside React. */
export function extensionSeen(): boolean {
    return seen;
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useExtensionPresent(): boolean {
    return useSyncExternalStore(
        subscribe,
        () => seen,
        () => false,
    );
}

const DONT_ASK_KEY = "jobenvoy.extension-hint.dismissed";

export function hintDismissed(): boolean {
    try {
        return window.localStorage.getItem(DONT_ASK_KEY) === "1";
    } catch {
        return false;
    }
}

export function dismissHint(): void {
    try {
        window.localStorage.setItem(DONT_ASK_KEY, "1");
    } catch {
        // A browser that keeps nothing asks again next time.
    }
}

const HAD_KEY = "jobenvoy.extension.seen";

/** Whether the extension said it was here on an earlier page in this browser. */
export function hadExtension(): boolean {
    try {
        return window.localStorage.getItem(HAD_KEY) === "1";
    } catch {
        return false;
    }
}

/** Keeps, or forgets, that this browser has the extension. */
export function rememberExtension(present: boolean): void {
    try {
        if (present) window.localStorage.setItem(HAD_KEY, "1");
        else window.localStorage.removeItem(HAD_KEY);
    } catch {
        // A browser that keeps nothing shows the banner until the extension speaks.
    }
}
